// Third party
import moment from 'moment';

// Utils
import { PROVINCES, REGISTERED_ACCOUNT_TYPES, FREQUENCIES, ACCOUNT_TYPES } from '../constants';
import { getAge } from '../time';
import { generateBSONId, generateTempId } from '../random';
import { LOCALE } from '../i18n';
import { getKeyByValue } from '../objects';

import {
  BMOi18n,
  GIF_PLAN_OPTIONS,
  GIF_SCHEME_TYPES,
  SALES_CHARGES,
  WITHDRAWAL_TYPES,
  INCOME_ACCOUNTS_DEPOSIT,
  maxAge71Accounts,
  MAX_REGISTERED_DEPOSIT,
  INVALID_AGE_REASON,
  PROJECTION_BASIS_TYPES,
  ADVERSE_SCENARIO_RATE,
  MAX_AGE_NON_REGISTERED_DEPOSIT,
} from './constants';
import { getMaturityDate, calculateScenario } from './calculate';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import Withdrawal from '@/types/bmo-gif/withdrawal';
import BMOContact from '@/types/bmo-gif/bmo-contact';

/**
 * Filter out provinces based on account types
 * @param {string} registeredAccountType Registered account type
 * @returns {string[]} Provinces to display
 */
export const getFilteredProvinces = (registeredAccountType?: string) => {
  let provinces: string[] = [];

  if (!registeredAccountType) {
    return [];
  }

  if (registeredAccountType === REGISTERED_ACCOUNT_TYPES.LIRA) {
    provinces = ['AB', 'MB', 'NB', 'NL', 'NS', 'ON', 'QC', 'SK'];
  } else if (registeredAccountType === REGISTERED_ACCOUNT_TYPES.LRSP) {
    provinces = ['FEDERAL', 'NT', 'NU', 'YT', 'BC'];
  } else {
    provinces = Object.keys(PROVINCES);
  }

  return provinces;
};

/**
 * Switch registered account type based on which province they select
 * @param {string} registeredAccountType Registered account type
 * @param {string} province Province
 * @returns {string} Updated registered account type
 */
export const switchRegisteredAccountType = (registeredAccountType: string, province: string) => {
  const LIRAtoLIFProvinces = [PROVINCES.ON, PROVINCES.QC, PROVINCES.AB, PROVINCES.NS, PROVINCES.MB, PROVINCES.NB, PROVINCES.NL];

  const LIRAtoPRIFProvinces = [PROVINCES.SK];

  const LRSPtoLIFProvinces = [PROVINCES.FEDERAL, PROVINCES.NT, PROVINCES.NU, PROVINCES.YT, PROVINCES.BC];

  const isLIRA = registeredAccountType === REGISTERED_ACCOUNT_TYPES.LIRA;
  const isLRSP = registeredAccountType === REGISTERED_ACCOUNT_TYPES.LRSP;

  if (isLIRA && !!LIRAtoLIFProvinces.find(val => val === province)) {
    return REGISTERED_ACCOUNT_TYPES.LIF;
  }

  if (isLIRA && !!LIRAtoPRIFProvinces.find(val => val === province)) {
    return REGISTERED_ACCOUNT_TYPES.PRIF;
  }

  if (isLRSP && !!LRSPtoLIFProvinces.find(val => val === province)) {
    return REGISTERED_ACCOUNT_TYPES.LIF;
  }

  return registeredAccountType;
};

/**
 * Returns the lower age of two date of births.
 * @param {Date} dob First date of birth
 * @param {Date | undefined} secondDob Second date of birth
 * @param {Date | undefined} purchaseDate Purchase date to use as reference in age calculation
 * @returns {number} Lower age between both date of births.
 */
export const getBaseAge = (dob: Date, secondDob?: Date, purchaseDate?: Date) => {
  const firstAge = getAge(dob, undefined, purchaseDate);

  let secondAge;

  if (secondDob) {
    secondAge = getAge(secondDob, undefined, purchaseDate);
  }

  return firstAge && secondAge && firstAge > secondAge ? secondAge : firstAge;
};

/**
 * Returns the earlier data of two date of births.
 * @param {Date} dob First date of birth
 * @param {Date | undefined} secondDob Second date of birth
 * @returns {Date} Earlier date between both date of births.
 */
export const getBaseDob = (dob: Date, secondDob?: Date) => (secondDob && moment(secondDob).isAfter(dob) ? secondDob : dob);

/**
 * Get the total gif portfolio allocation
 *
 * @param {Portfolio} portfolio Portfolio.
 * @return {number} Portfolio allocation
 */
export const getTotalGifPortfolioAllocation = (portfolio: BMOGIFPortfolio) => {
  const totalWeight = portfolio.holdings.reduce((total, holding) => (total += holding.weight), 0);
  return Math.round(totalWeight * 100000) / 100000;
};

/**
 * Gets the market return less a specific percentage for CLHIA compliance
 * @param marketReturns the portfolio's market returns based on its equity proportion
 * @param rate the rate to subtract
 */
export const calculateAdverseMarketReturn = (marketReturns: [number, number][], rate: number = ADVERSE_SCENARIO_RATE) =>
  marketReturns.slice().map(item => {
    const marketReturn = item.slice() as [number, number];
    const adverse = marketReturn[1] / 100 - (1 + rate / 100) ** (1 / 12) + 1;
    marketReturn[1] = adverse * 100;
    return marketReturn;
  });

/**
 * Generate base GIF scenario
 * @param {Portfolio} portfolio BMO GIF portfolio
 * @param {Date} baseDob younger age to use from contact
 * @param {Withdrawal} withdrawal withdrawal Data
 * @return {BMOGIFScenario} calculated GIF scenario
 */

export const createScenario = (portfolio: BMOGIFPortfolio, withdrawal?: Withdrawal, temp?: boolean): BMOGIFScenario => {
  const scenario: BMOGIFScenario = {
    _id: temp ? generateTempId() : generateBSONId(),
    purchaseDate: new Date(portfolio.purchaseDate),
    maturityDate: getMaturityDate(portfolio.term || 0, new Date(portfolio.purchaseDate)),
    term: portfolio.term || 0,
    deposit: {
      amount: portfolio.subsequentDepositAmount || 0,
      frequency: portfolio.frequency || FREQUENCIES.MONTHLY,
      scheme: portfolio.scheme || GIF_SCHEME_TYPES.AGE,
      numYears: portfolio.numYears || 0,
      start: portfolio.start || 0,
      end: portfolio.end || 0,
    },
    withdrawal,
    initialValue: portfolio.depositAmount || 0,
    mer: portfolio.mer,
    salesCharge: {
      type: portfolio.salesChargeType || SALES_CHARGES.FEL,
      percent: portfolio.salesChargeAmount || 0,
    },
    series: portfolio.planOption || GIF_PLAN_OPTIONS.GIF7575,
    dob: new Date(portfolio.dob),
    deathBenefitFee: portfolio.deathBenefitFee,
    deathBenefitReset: portfolio.deathGuaranteeReset,
    deathBenefitResetEnhanced: portfolio.deathGuaranteeResetEnhanced,
    averageMarketReturn: portfolio.averageMarketReturn,
    data: [],
    adverseData: [],
    province: portfolio.province || PROVINCES.FEDERAL,
  };
  const isCustomReturn = portfolio.projectionBasis && portfolio.projectionBasis.type === PROJECTION_BASIS_TYPES.CUSTOM && portfolio.customRate != null;
  scenario.data = calculateScenario(scenario, portfolio.marketReturn, isCustomReturn ? portfolio.customRate : undefined);
  const adverseMarketReturn = calculateAdverseMarketReturn(portfolio.marketReturn);
  scenario.adverseData = calculateScenario(scenario, adverseMarketReturn, isCustomReturn ? (portfolio.customRate || 0) - ADVERSE_SCENARIO_RATE : undefined);
  return scenario;
};

/**
 * Creates a draft BMO GIF item
 * @return {BMOGIFItem} new BMO GIF item
 */
export const portfolioToBmoGifItem = (portfolio: BMOGIFPortfolio): BMOGIFItem =>
  ({
    _id: generateBSONId(),
    name: portfolio.name,
    portfolio,
    scenarios: [],
    baseScenario: createScenario(portfolio),
  }) as BMOGIFItem;

/**
 * Validates the maturity date against the contract maturity date
 *
 * @param {string} planOption Plan Option
 * @param {Date} purchaseDate Purchase date
 * @param {number} purchaseAge Purchase age
 * @param {Date} contractMaturityDate Date when annuitant turns 100
 * @param {number | undefined} term Term length
 * @return {boolean} `true` if contract maturity date is on the same day or after maturity date. `false` otherwise
 */
export const validMaturityDate = (planOption: string, purchaseDate: Date, purchaseAge?: number, contractMaturityDate?: Date, term?: number) => {
  if (planOption === GIF_PLAN_OPTIONS.GIF100100 && term) {
    const maturityDate = getMaturityDate(term, new Date(purchaseDate));

    return moment(contractMaturityDate).isSameOrAfter(maturityDate);
  }

  return !!purchaseAge && purchaseAge < 100;
};

/**
 * Validates the required inputs for a GIF portfolio
 *
 * @param {BMOGIFPortfolio} portfolio GIF Portfolio
 * @return {boolean} `true` if all inputs are valid. `false` otherwise.
 */
export const validateBMOGIFPortfolio = (portfolio: BMOGIFPortfolio) => {
  const { depositAmount, salesChargeType, projectionBasis, planOption, purchaseDate, dob, contractMaturityDate, term, start, end, scheme } = portfolio;

  const age = getAge(dob, undefined, purchaseDate);

  const validateHoldings = getTotalGifPortfolioAllocation(portfolio) === 100;
  const validateMaturityDate = !!planOption && validMaturityDate(planOption, purchaseDate, age, contractMaturityDate, term);
  let validateSubsequentDeposit = true;

  if (scheme === GIF_SCHEME_TYPES.AGE && start && end && age) {
    // Set to false so we don't calculate portfolio
    if (start < age || end <= start) {
      validateSubsequentDeposit = false;
    }
  }

  return validateHoldings && validateSubsequentDeposit && depositAmount !== undefined && !!projectionBasis && !!salesChargeType && validateMaturityDate;
};

/**
 * Return localized string containing lump sum info
 *
 * @param {BMOGIFScenario} scenario GIF Scenario
 * @param {string} locale locale
 * @return {string} Lump Sum info string
 */
export const getLumpSumInfo = (scenario: BMOGIFScenario, locale: string) => {
  const { withdrawal } = scenario;

  if (!withdrawal || !withdrawal.lumpSum) {
    return `${BMOi18n[locale].SCENARIOS.INCOMPLETE}`;
  }

  const { amount, scheme, at, month } = withdrawal.lumpSum;

  if (!amount || !at) {
    return `${BMOi18n[locale].SCENARIOS.INCOMPLETE}`;
  }

  const amountString = locale === LOCALE.EN_CA ? `$${amount}` : `${amount}$`;
  let withdrawalString;

  if (scheme === GIF_SCHEME_TYPES.AGE) {
    withdrawalString = locale === LOCALE.EN_CA ? `${BMOi18n[locale].AGE} ${at}` : `${at} ${BMOi18n[locale].AGE_AND_YEARS.YEARS}`;
  }

  if (scheme === GIF_SCHEME_TYPES.YEAR) {
    withdrawalString = `${BMOi18n[locale].AGE_AND_YEARS.IN_YEAR} ${at} ${BMOi18n[locale].AGE_AND_YEARS.MONTH} ${month || 12}`;
  }

  return `${amountString}, ${withdrawalString}`;
};

/**
 * Return localized string containing lump sum info
 *
 * @param {BMOGIFScenario} scenario GIF Scenario
 * @param {string} locale locale
 * @return {string} Systematic info string
 */
export const getSystematicInfo = (scenario: BMOGIFScenario, item: BMOGIFItem, locale: string) => {
  const { withdrawal } = scenario;

  if (!withdrawal || !withdrawal.systematic || !withdrawal.systematic.type) {
    return `${BMOi18n[locale].SCENARIOS.INCOMPLETE}`;
  }

  const { amount, type, scheme, start, end, numYears, frequency, startMonth, endMonth } = withdrawal.systematic;
  const age = getAge(item.portfolio.dob, undefined, item.portfolio.purchaseDate);

  const amountString = locale === LOCALE.EN_CA ? `$${amount}` : `${amount}$`;
  let withdrawalString;
  const typeKey = getKeyByValue(WITHDRAWAL_TYPES, type);
  const typeString = typeKey ? BMOi18n[locale].WITHDRAWAL_TYPES[typeKey] : '';

  const frequencyKey = getKeyByValue(FREQUENCIES, frequency);
  const frequencyString = frequencyKey ? BMOi18n[locale][frequencyKey] : '';

  switch (type) {
    case WITHDRAWAL_TYPES.RRIF:
    case WITHDRAWAL_TYPES.LIF_MAX: {
      if (start === undefined) {
        return `${BMOi18n[locale].SCENARIOS.INCOMPLETE}`;
      }

      withdrawalString = `${BMOi18n[locale].AGE_AND_YEARS.STARTING_AGE} ${start}`;

      if (end && type === WITHDRAWAL_TYPES.LIF_MAX) {
        withdrawalString += ` ${BMOi18n[locale].AGE_AND_YEARS.TO} ${end}`;
      }

      break;
    }
    case WITHDRAWAL_TYPES.CUSTOM: {
      if (scheme === GIF_SCHEME_TYPES.AGE) {
        if (!amount || start === undefined || !end || !frequency || !age || (start || 0) < age) {
          return `${BMOi18n[locale].SCENARIOS.INCOMPLETE}`;
        }

        withdrawalString =
          locale === LOCALE.EN_CA
            ? `${amountString}, ${BMOi18n[locale].AGE_AND_YEARS.AGE} ${start} ${BMOi18n[locale].AGE_AND_YEARS.TO} ${end}, ${frequencyString}`
            : `${amountString}, ${start} ${BMOi18n[locale].AGE_AND_YEARS.TO} ${end} ${BMOi18n[locale].AGE_AND_YEARS.AGE}, ${frequencyString}`;
      }

      if (scheme === GIF_SCHEME_TYPES.YEAR) {
        if (!amount || start === undefined || !end || !frequency || !age) {
          return `${BMOi18n[locale].SCENARIOS.INCOMPLETE}`;
        }

        withdrawalString = `${amountString} ${BMOi18n[locale].AGE_AND_YEARS.FOR} ${numYears} ${BMOi18n[locale].AGE_AND_YEARS.YEARS}, 
        ${BMOi18n[locale].AGE_AND_YEARS.FROM_MONTH} ${startMonth || 1} ${BMOi18n[locale].AGE_AND_YEARS.TO} ${endMonth || 12}, ${frequencyString}`;
      }

      break;
    }
    default: {
      withdrawalString = '';
    }
  }

  return `${typeString}, ${withdrawalString}`;
};

/**
 * Return localized string containing maturity guarantee disclaimer
 *
 * @param {string} planOption GIF Plan option
 * @param {string} locale locale
 * @return {string} Maturity Guarantee disclaimer
 */
export const getMaturityGuaranteeDisclaimer = (locale: string, planOption?: string) => {
  if (!planOption) {
    return '';
  }

  let disclaimer = BMOi18n[locale].GIF_GRAPH.LEGEND.MATURITY_GUARANTEE;
  if (planOption === GIF_PLAN_OPTIONS.GIF100100) {
    disclaimer = BMOi18n[locale].GIF_GRAPH.LEGEND.MATURITY_GUARANTEE_100100;
  }

  return disclaimer;
};

/**
 * Return localized string containing death benefit disclaimer
 *
 * @param {string} planOption GIF Plan option
 * @param {string} locale locale
 * @return {string} Death Benefit disclaimer
 */
export const getDeathBenefitDisclaimer = (locale: string, planOption?: string) => {
  if (!planOption) {
    return '';
  }

  let disclaimer;

  switch (planOption) {
    case GIF_PLAN_OPTIONS.GIF7575: {
      disclaimer = BMOi18n[locale].GIF_GRAPH.LEGEND.DEATH_BENEFIT_GUARANTEE_7575;
      break;
    }
    case GIF_PLAN_OPTIONS.GIF75100: {
      disclaimer = BMOi18n[locale].GIF_GRAPH.LEGEND.DEATH_BENEFIT_GUARANTEE_75100;
      break;
    }
    case GIF_PLAN_OPTIONS.GIF75100PLUS: {
      disclaimer = BMOi18n[locale].GIF_GRAPH.LEGEND.DEATH_BENEFIT_GUARANTEE_75100PLUS;
      break;
    }
    case GIF_PLAN_OPTIONS.GIF100100: {
      disclaimer = BMOi18n[locale].GIF_GRAPH.LEGEND.DEATH_BENEFIT_GUARANTEE_100100;
      break;
    }
  }

  return disclaimer;
};

export const scenarioHasWithdrawal = (scenario: BMOGIFScenario) =>
  scenario.withdrawal && (scenario.withdrawal.systematic.amount > 0 || scenario.withdrawal.lumpSum.amount > 0);

export const isInvalidDepositAge = (portfolio: BMOGIFPortfolio, contact: BMOContact): { reason?: string; isInvalid: boolean } => {
  const { planOption, purchaseDate, dob, end, scheme, contractMaturityDate, term } = portfolio;
  const { accountType, registeredAccountType } = contact;
  const is75Series = planOption === GIF_PLAN_OPTIONS.GIF7575 || planOption === GIF_PLAN_OPTIONS.GIF75100PLUS || planOption === GIF_PLAN_OPTIONS.GIF75100;

  const purchaseAge = getAge(dob, undefined, new Date(new Date(purchaseDate).getFullYear(), 11, 31)) || 0;
  let maxDepositAge = 0;
  if (end !== undefined && scheme === GIF_SCHEME_TYPES.YEAR) {
    const endDate = new Date(Date.UTC(end + new Date(purchaseDate).getFullYear(), 11, 31));
    maxDepositAge = getAge(dob, undefined, endDate) || 0;
  }
  if (end !== undefined && scheme === GIF_SCHEME_TYPES.AGE) {
    maxDepositAge = end;
  }
  if (planOption && contractMaturityDate && purchaseDate && !validMaturityDate(planOption, purchaseDate, purchaseAge, contractMaturityDate, term)) {
    return { isInvalid: true };
  }

  if (!planOption) {
    return { isInvalid: false };
  }

  if (accountType === ACCOUNT_TYPES.TFSA || accountType === ACCOUNT_TYPES.NON_REGISTERED) {
    if (purchaseAge > MAX_AGE_NON_REGISTERED_DEPOSIT[planOption]) {
      return { reason: INVALID_AGE_REASON.INITIAL_DEPOSIT, isInvalid: true };
    }
    if (maxDepositAge > MAX_AGE_NON_REGISTERED_DEPOSIT[planOption]) {
      return { reason: INVALID_AGE_REASON.SUBSEQUENT_DEPOSIT, isInvalid: true };
    }
  } else if (accountType === ACCOUNT_TYPES.REGISTERED) {
    if (registeredAccountType && maxAge71Accounts.includes(registeredAccountType)) {
      if (purchaseAge > MAX_REGISTERED_DEPOSIT) {
        return { reason: INVALID_AGE_REASON.INITIAL_DEPOSIT, isInvalid: true };
      }
      if (maxDepositAge > MAX_REGISTERED_DEPOSIT) {
        return { reason: INVALID_AGE_REASON.SUBSEQUENT_DEPOSIT, isInvalid: true };
      }
    }
    if (registeredAccountType && planOption && Object.keys(INCOME_ACCOUNTS_DEPOSIT).includes(registeredAccountType)) {
      const maxAge = INCOME_ACCOUNTS_DEPOSIT[registeredAccountType][planOption];
      if (purchaseAge > maxAge) {
        return { reason: INVALID_AGE_REASON.INITIAL_DEPOSIT, isInvalid: true };
      }
      if (maxDepositAge > maxAge) {
        return { reason: INVALID_AGE_REASON.SUBSEQUENT_DEPOSIT, isInvalid: true };
      }
    }
  }
  return { isInvalid: false };
};
